import React, { useState, useEffect, memo, useMemo, useContext } from 'react';
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import { DetachedWindowContext } from '../WindowPortal';

interface CardImageProps {
    card: string;
    small?: boolean;
    medium?: boolean;
    extraSmall?: boolean;
    micro?: boolean;
    tiny?: boolean;
    community?: boolean;
    highlighted?: boolean;
    winner?: boolean;
    style?: React.CSSProperties;
    type?: 'light' | 'dark';
    size?: number;
}

// Cache for preloaded images to avoid redundant loading
const imageCache: Record<string, string> = {};
const suitImageCache: Record<string, string> = {};

// Secondary cache for detached windows
const detachedImageCache: Record<string, string> = {};
const detachedSuitImageCache: Record<string, string> = {};

const StyledImg = styled.img<{ isHighlighted: boolean; tiny?: boolean }>`
  border: ${props => props.isHighlighted ? '2px solid #3de361' : 'none'} !important;
  border-radius: ${props => props.tiny ? '0px' : '4px'} !important;
`;

// Function to preload all card images at once
export const preloadAllCardImages = (): Promise<void> => {
    const suits = ['H', 'D', 'C', 'S'];
    const values = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
    const backs = ['1B', '2B'];
    const suitNames = ['spade', 'heart', 'diamond', 'club'];
    
    // Create an array of all image loading promises
    const promises: Promise<void>[] = [];
    
    // Add card faces
    suits.forEach(suit => {
        values.forEach(value => {
            const card = `${value}${suit}`;
            if (!imageCache[card]) {
                const promise = new Promise<void>((resolve) => {
                    const img = new Image();
                    img.src = `${import.meta.env.BASE_URL}images/deck-01/${card}.svg`;
                    img.onload = () => {
                        imageCache[card] = img.src;
                        resolve();
                    };
                    img.onerror = () => {
                        console.error(`Failed to preload image: ${card}.svg`);
                        resolve();
                    };
                });
                promises.push(promise);
            }
        });
    });
    
    // Add card backs
    backs.forEach(back => {
        if (!imageCache[back]) {
            const promise = new Promise<void>((resolve) => {
                const img = new Image();
                img.src = `${import.meta.env.BASE_URL}images/deck-01/${back}.svg`;
                img.onload = () => {
                    imageCache[back] = img.src;
                    resolve();
                };
                img.onerror = () => {
                    console.error(`Failed to preload image: ${back}.svg`);
                    resolve();
                };
            });
            promises.push(promise);
        }
    });
    
    // Add suit images
    suitNames.forEach(suitName => {
        if (!suitImageCache[suitName]) {
            const promise = new Promise<void>((resolve) => {
                const img = new Image();
                img.src = `${import.meta.env.BASE_URL}images/suits/${suitName}.svg`;
                img.onload = () => {
                    suitImageCache[suitName] = img.src;
                    resolve();
                };
                img.onerror = () => {
                    console.error(`Failed to preload suit image: ${suitName}.svg`);
                    resolve();
                };
            });
            promises.push(promise);
        }
    });
    
    // Return a promise that resolves when all images are loaded
    return Promise.all(promises).then(() => {
    }).catch(error => {
        console.error('Error preloading card images:', error);
    });
};

// Start preloading images immediately in the background
setTimeout(() => {
    preloadAllCardImages();
}, 0);

const CardImage: React.FC<CardImageProps> = memo(({ 
    card, 
    small = false, 
    medium = false,
    extraSmall = false,
    tiny = false,
    micro = false,
    community = false, 
    highlighted = false, 
    winner = false, 
    style,
    type = 'light',
    size 
}) => {
    // Check if we're in a detached window context
    const detachedWindow = useContext(DetachedWindowContext);
    
    // Use the appropriate cache based on context
    const currentImageCache = detachedWindow ? detachedImageCache : imageCache;
    const currentSuitImageCache = detachedWindow ? detachedSuitImageCache : suitImageCache;
    
    const [cardImage, setCardImage] = useState<string | undefined>(currentImageCache[card]);
    const [suitImage, setSuitImage] = useState<string | undefined>(
        micro ? currentSuitImageCache[
            card.slice(-1).toLowerCase() === 's' ? 'spade' : 
            card.slice(-1).toLowerCase() === 'h' ? 'heart' : 
            card.slice(-1).toLowerCase() === 'd' ? 'diamond' : 
            'club'
        ] : undefined
    );
    
    // Force reload images when in detached window
    const [loadAttempts, setLoadAttempts] = useState(0);

    useEffect(() => {
        // If image is already in cache, no need to load
        if (currentImageCache[card]) {
            setCardImage(currentImageCache[card]);
            return;
        }

        const img = new Image();
        img.src = `${import.meta.env.BASE_URL}images/deck-01/${card}.svg`;
        
        // Add timestamp to prevent caching issues in detached window
        if (detachedWindow) {
            img.src = `${import.meta.env.BASE_URL}images/deck-01/${card}.svg?t=${Date.now()}`;
        }
        
        img.onload = () => {
            currentImageCache[card] = img.src;
            setCardImage(img.src);
        };
        
        img.onerror = () => {
            console.error(`Failed to load image: ${card}.svg`);
            // Retry loading up to 3 times with increasing delay
            if (loadAttempts < 3) {
                setTimeout(() => {
                    setLoadAttempts(prev => prev + 1);
                }, 300 * (loadAttempts + 1));
            }
        };

        if (micro) {
            const suit = card.slice(-1).toLowerCase();
            const suitName = suit === 's' ? 'spade' : 
                            suit === 'h' ? 'heart' : 
                            suit === 'd' ? 'diamond' : 
                            'club';
            
            // If suit image is already in cache, use it
            if (currentSuitImageCache[suitName]) {
                setSuitImage(currentSuitImageCache[suitName]);
                return;
            }
            
            const suitImg = new Image();
            suitImg.src = `${import.meta.env.BASE_URL}images/suits/${suitName}.svg`;
            
            // Add timestamp to prevent caching issues in detached window
            if (detachedWindow) {
                suitImg.src = `${import.meta.env.BASE_URL}images/suits/${suitName}.svg?t=${Date.now()}`;
            }
            
            suitImg.onload = () => {
                currentSuitImageCache[suitName] = suitImg.src;
                setSuitImage(suitImg.src);
            };
            
            suitImg.onerror = () => console.error(`Failed to load suit image for ${suit}`);
        }
    }, [card, micro, detachedWindow, loadAttempts, currentImageCache, currentSuitImageCache]);

    // Memoize these calculations to avoid recalculating on every render
    const cardSize = useMemo(() => {
        if (size) return `${size}px`;
        if (micro) return '24px';
        if (extraSmall) return '20px';
        if (tiny) return '17px';
        if (small && !winner) return '48px';
        if (small && winner) return '80px';
        if (medium) return isMobile ? '18px' : '40px';
        if (community) return '100%';
        return '106px';
    }, [size, micro, extraSmall, tiny, small, winner, medium, community, isMobile]);

    const getCardType = () => {
        if (type === 'light') return `${import.meta.env.BASE_URL}images/deck-01/1B.svg`;
        if (type === 'dark') return `${import.meta.env.BASE_URL}images/deck-01/2B.svg`;
    };

    const cardType = getCardType();

    if (micro) {
        const value = card.slice(0, -1);
        const displayValue = value === 'T' ? '10' : value;
        return (
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: card.slice(-1).match(/[HD]/) ? '#ff0000' : '#000000',
                ...style
            }}>
                <span>{displayValue}</span>
                {suitImage && (
                    <img 
                        src={suitImage} 
                        alt="" 
                        style={{ 
                            width: '12px', 
                            height: '12px',
                            verticalAlign: 'middle'
                        }} 
                    />
                )}
            </div>
        );
    }

    return (
        <StyledImg
            className='card-front'
            src={cardImage || cardType}
            alt={card}
            isHighlighted={highlighted}
            tiny={tiny}
            style={{
                width: cardSize,
                height: 'auto',
                verticalAlign: 'middle',
                ...style,
            }}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.card === nextProps.card &&
        prevProps.highlighted === nextProps.highlighted &&
        prevProps.winner === nextProps.winner &&
        prevProps.type === nextProps.type &&
        prevProps.size === nextProps.size &&
        prevProps.small === nextProps.small &&
        prevProps.medium === nextProps.medium &&
        prevProps.extraSmall === nextProps.extraSmall &&
        prevProps.tiny === nextProps.tiny &&
        prevProps.micro === nextProps.micro &&
        prevProps.community === nextProps.community
    );
});

export default CardImage;
